import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { User, AuthContextType, RegisterData, UserPermissions } from '../types';
import { authAPI } from '../services/api';
import { setTokens } from '../services/api';
import { usePostHog } from '@posthog/react';
import { clearSuperAdmin } from '../lib/superAdmin';
import { queryClient } from '../lib/queryClient';
import { toast } from 'react-toastify';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const resolveAuthProvider = (u: any, hint?: string): string => {
  if (hint) return hint;
  if (u?.googleId && !u?.hasPassword) return 'google';
  if (u?.googleId && u?.hasPassword) return 'google+email';
  return 'email';
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const posthog = usePostHog();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const lastIdentifiedUserIdRef = React.useRef<string>('');

  /* Cross-user cache isolation.
   *
   * setTokens() in services/api.ts clears the axios-level `getCache` on every
   * token change - added 19 Aug after a manager saw a scenario carrying the
   * AUTHOR's `canPractise: true` from the previous user's cached payload. That
   * fix covered ONE cache. react-query is a second, entirely independent cache
   * holding the same responses, and nothing ever cleared it: `queryClient.clear()`
   * appeared nowhere in the app. So a logout followed by a login in the same tab
   * left every dashboard / pathways / history query populated with the previous
   * account's data, which the next user's hooks read as `isPending: false` and
   * rendered immediately.
   *
   * Keyed on the resolved user id rather than on the token, because a routine
   * silent refresh also changes the token - and clearing on that would blank
   * every mounted query back to a skeleton every ~15 minutes for no reason.
   * Identity A -> B and A -> null (logout, session expiry) both clear; the
   * initial null -> A hydration does not, since there is nothing cached yet. */
  const cachedIdentityRef = React.useRef<string | null>(null);
  useEffect(() => {
    const id = user ? String((user as any)._id || (user as any).id || user.email || '') : null;
    const previous = cachedIdentityRef.current;
    cachedIdentityRef.current = id;
    if (previous !== null && previous !== id) {
      try {
        queryClient.clear();
      } catch {
        /* cache reset is best-effort */
      }
    }
  }, [user]);

  const identifyUser = (u: any, authProviderHint?: string) => {
    try {
      if (!u) {
        console.warn('[PostHog] identifyUser called with null/undefined user');
        return;
      }
      const uid = String(u?._id || u?.id || '').trim();
      if (!uid) {
        console.warn('[PostHog] identifyUser: no uid found on user', u);
        return;
      }

      const currentDistinctId = posthog?.get_distinct_id?.();
      if (lastIdentifiedUserIdRef.current === uid && currentDistinctId === uid) {
        return;
      }

      lastIdentifiedUserIdRef.current = uid;
      const provider = resolveAuthProvider(u, authProviderHint);
      const displayName = u?.name || u?.email || '';
      // The workspace name when /me carries it, else the name given at sign-up.
      const companyName = String(u?.companyName || u?.company || '').trim();

      console.log('[PostHog] Identifying user:', {
        uid,
        email: u?.email,
        name: displayName,
        auth_provider: provider,
        previousDistinctId: currentDistinctId,
      });

      posthog?.identify(
        uid,
        {
          $email: u?.email,
          $name: displayName,
          email: u?.email,
          name: displayName,
          role: u?.role,
          companyId: u?.companyId,
          ...(companyName ? { company_name: companyName } : {}),
          auth_provider: provider,
        },
        {
          initial_auth_provider: provider,
          signed_up_at: new Date().toISOString(),
        },
      );
      // Cohort stamp on every event from here on. Pages that fire events
      // without the auth context to hand (the report, read back through
      // posthog.get_property) get the same id the person was identified
      // with; reset() on logout clears it.
      posthog?.register({ company_id: u?.companyId || '' });
    } catch (e) {
      console.error('[PostHog] identify failed:', e);
    }
  };

  useEffect(() => {
    // Auth-init timeout. 5s was too tight: a cold-boot /me path that
    // hits multiple Dynamo queries (teams, owner-of-company,
    // subscription mirror) routinely takes 6–8s on the first request
    // after a backend restart, which would fire a spurious timeout and
    // sign the user out on hard refresh. 15s matches the per-request
    // watchdog in backend/server.js and is still tight enough to flag a
    // genuinely stuck refresh path.
    const AUTH_INIT_TIMEOUT_MS = 15000;
    const withTimeout = <T,>(
      promise: Promise<T>,
      step: 'refresh' | 'getCurrentUser',
    ): Promise<T> => {
      return new Promise<T>((resolve, reject) => {
        const timer = setTimeout(() => {
          try {
            posthog?.capture('auth_init_timeout', { step, timeout_ms: AUTH_INIT_TIMEOUT_MS });
          } catch {}
          reject(new Error(`auth_init_timeout:${step}`));
        }, AUTH_INIT_TIMEOUT_MS);
        promise.then(
          (v) => {
            clearTimeout(timer);
            resolve(v);
          },
          (e) => {
            clearTimeout(timer);
            reject(e);
          },
        );
      });
    };

    const initAuth = async () => {
      try {
        let refreshSuccess = false;
        try {
          const refreshResp = await withTimeout(
            authAPI.refresh?.() ?? Promise.resolve(null as any),
            'refresh',
          );
          if (refreshResp?.accessToken && refreshResp?.csrfToken) {
            setTokens(refreshResp.accessToken, refreshResp.csrfToken);
            refreshSuccess = true;
          }
        } catch {
          setUser(null);
          setLoading(false);
          return;
        }

        if (refreshSuccess) {
          try {
            const response = await withTimeout(authAPI.getCurrentUser(), 'getCurrentUser');
            setUser(response.user);
            identifyUser(response.user);
          } catch {
            setUser(null);
          }
        }
      } catch {
        setUser(null);
      }
      setLoading(false);
    };

    initAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for session expiry from the API interceptor (replaces hard redirect)
  useEffect(() => {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent;
      const message =
        customEvent.detail?.message || 'Your session has expired. Please log in again.';

      // Show toast notification with session expiry message
      toast.error(message, { autoClose: 3000 });

      posthog?.reset();
      lastIdentifiedUserIdRef.current = '';
      setUser(null);

      /* Already on /login? Then there is nothing to redirect to, and doing it
       * anyway is a FULL PAGE LOAD that boots the app, refreshes, fails the
       * same way and fires this handler again — the refresh loop people see
       * after leaving a tab open overnight. The state has been cleared and the
       * toast has been shown; that is the whole job here. */
      if (window.location.pathname === '/login') return;

      // Otherwise redirect after a brief delay so the toast is readable.
      setTimeout(() => {
        window.location.href = '/login';
      }, 1000);
    };
    window.addEventListener('auth:session-expired', handler);
    return () => window.removeEventListener('auth:session-expired', handler);
  }, [posthog]);

  /**
   * Top up the freshly-authenticated user with the fields only GET /auth/me
   * builds — `teamIds`, `teamIdsManaged` and `teams`.
   *
   * The login / register / google responses return the user record straight
   * from the store, which has none of that: team context needs two lookups the
   * auth routes don't do. Until the next full page load called /auth/me, the
   * app ran with a user who appeared to belong to no team and manage nothing.
   *
   * That was not cosmetic. /practice bucketing keys the review queues off
   * `teamIdsManaged`, so a manager who signed in and went straight to Practice
   * saw NO submissions queue at all until they happened to reload — the
   * scenario reached their account and the UI silently had nowhere to put it.
   *
   * Best-effort: if the call fails we keep the login payload rather than
   * dropping the user back to signed-out.
   */
  const hydrateTeamContext = async (fallback: any) => {
    try {
      const fresh = await authAPI.getCurrentUser();
      if (fresh?.user) {
        setUser(fresh.user);
        return fresh.user;
      }
    } catch {
      /* keep the login payload */
    }
    return fallback;
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await authAPI.login({ email, password });
      if (response.accessToken && response.csrfToken) {
        setTokens(response.accessToken, response.csrfToken);
      }
      setUser(response.user);
      await hydrateTeamContext(response.user);

      if (response.user) {
        identifyUser(response.user, 'email');
        posthog?.capture('user_logged_in', {
          auth_provider: 'email',
        });
      }
      return response;
    } catch (error) {
      throw error;
    }
  };

  /** Sign in with an emailed one-time code.
   *  The backend returns the same payload as /auth/login, so the session is
   *  established exactly as the password path does - same tokens, same team
   *  hydration. Only the analytics provider differs, so we can see how many
   *  people actually use codes. */
  const loginWithOtp = async (email: string, code: string) => {
    const response = await authAPI.verifyLoginOtp(email, code);
    if (response.accessToken && response.csrfToken) {
      setTokens(response.accessToken, response.csrfToken);
    }
    setUser(response.user);
    await hydrateTeamContext(response.user);

    if (response.user) {
      identifyUser(response.user, 'email');
      posthog?.capture('user_logged_in', {
        auth_provider: 'email_otp',
      });
    }
    return response;
  };

  const register = async (userData: RegisterData) => {
    try {
      const response = await authAPI.register(userData);

      // If email verification is required, don't set tokens (backend doesn't send them)
      if (response.requiresVerification) {
        return response;
      }

      if (response.accessToken && response.csrfToken) {
        setTokens(response.accessToken, response.csrfToken);
      }
      setUser(response.user);
      await hydrateTeamContext(response.user);

      if (response.user) {
        identifyUser(response.user, 'email');
        posthog?.capture('user_signed_up', {
          auth_provider: 'email',
          email: response.user.email,
          name: response.user.name,
        });
      }
      return response;
    } catch (error) {
      throw error;
    }
  };

  const googleLogin = async (
    credential: string,
    company?: string,
    department?: string,
    position?: string,
    extra?: {
      companyId?: string;
      /** The team half of a team invite link (/join). */
      teamId?: string;
      companyName?: string;
      isAdmin?: boolean;
      role?: 'employee' | 'manager' | 'admin';
      lanuraInvite?: string;
      mode?: 'login';
      selfServeOwner?: boolean;
    },
  ) => {
    try {
      const response = await authAPI.googleAuth(credential, company, department, position, extra);
      if (response.accessToken && response.csrfToken) {
        setTokens(response.accessToken, response.csrfToken);
      }
      setUser(response.user);
      await hydrateTeamContext(response.user);

      if (response.user) {
        identifyUser(response.user, 'google');
        if (response.isNewUser) {
          posthog?.capture('user_signed_up', {
            auth_provider: 'google',
            email: response.user.email,
            name: response.user.name,
          });
        } else {
          posthog?.capture('user_logged_in', {
            auth_provider: 'google',
          });
        }
      } else {
        console.warn('[PostHog] Google OAuth: response.user is null/undefined', response);
      }
      return response;
    } catch (error) {
      console.error('[PostHog] Google OAuth handler error:', error);
      throw error;
    }
  };

  const logout = async () => {
    posthog?.capture('user_logged_out');

    try {
      await authAPI.logout?.();
    } catch {
      // ignore
    } finally {
      console.log('[PostHog] Resetting identity on logout');
      posthog?.reset();
      lastIdentifiedUserIdRef.current = '';
      setTokens(null, null);
      setUser(null);
      // Clear per-session scratch state so the next user on this browser
      // can't fall back to the previous user's review/practice context.
      try {
        localStorage.removeItem('currentSessionId');
      } catch {}
      // superAdmin break-glass toggle must never persist across sessions/accounts.
      try {
        clearSuperAdmin();
      } catch {}
    }
  };

  // Memoized so consumers can put `refreshUser` in a useEffect dependency
  // array without retriggering the effect on every AuthProvider re-render.
  // Without this, Dashboard.tsx:87-91 (which calls refreshUser when
  // onboarding isn't yet completed) created an infinite loop: refreshUser
  // updates `user` → AuthProvider re-renders → new refreshUser reference →
  // Dashboard's effect deps "change" → effect fires → refreshUser called
  // again → loop. The closure captures `setUser` (stable) and
  // `identifyUser`/`posthog` (stable enough — only used for analytics
  // side-effects; the initial-render capture is acceptable).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const refreshUser = useCallback(async () => {
    const me = await authAPI.getCurrentUser();
    setUser(me.user);
    identifyUser(me.user);
  }, []);

  const setUserFromProfile = (u: Partial<User>) => {
    setUser((prev) => (prev ? ({ ...prev, ...u } as User) : (u as User)));
  };

  /**
   * Returns true if the current user has the given permission.
   * Falls back to role-based defaults when the permissions object is absent
   * (e.g. existing users who haven't been through a permission migration yet).
   */
  const can = (permission: keyof UserPermissions): boolean => {
    if (!user) return false;
    // `permissions` comes from /auth/me with the role defaults already merged
    // under any admin override, so it is the same answer the server will give.
    if (user.permissions && permission in user.permissions) {
      return !!user.permissions[permission];
    }
    // Fallback defaults (mirrors backend defaultPermissionsForRole), for a
    // response that predates a capability.
    const role = user.role;
    const ownerOrAdmin = role === 'owner' || role === 'admin';
    const managerOrAbove = ownerOrAdmin || role === 'manager';
    if (permission === 'manageBilling') return role === 'owner';
    if (permission === 'manageTeam') return managerOrAbove;
    if (permission === 'assignSimulations') return managerOrAbove;
    if (permission === 'viewAnalytics') return true; // all roles; scope enforced by backend
    if (permission === 'exportReports') return ownerOrAdmin;
    if (permission === 'manageRoles') return ownerOrAdmin;
    // A manager reads the framework and never changes it.
    if (permission === 'viewFramework') return managerOrAbove;
    if (permission === 'manageFramework') return ownerOrAdmin;
    if (permission === 'manageCompanySettings') return ownerOrAdmin;
    // Admin and owner only for now (D15). A manager's team shelf comes later.
    if (permission === 'manageTeamResources') return ownerOrAdmin;
    return false;
  };

  const value: AuthContextType = {
    user,
    login,
    register,
    googleLogin,
    loginWithOtp,
    logout,
    loading,
    refreshUser,
    setUserFromProfile,
    can,
  } as any;

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
